import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, MessageCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import blogService from '../../../services/blogService';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage';
import CommentSection from './CommentSection';

const BlogPost = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await blogService.getPostBySlug(slug);
      setPost(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const createMarkup = (content) => ({ __html: content });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={fetchPost} />;
  if (!post) return <ErrorMessage message="Post not found" />;

  return (
    <div className="max-w-3xl mx-auto py-8 pb-16">
      {/* Back link */}
      <motion.div
        initial={{ opacity: 0, x: -15 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <Link
          to="/blog"
          className={`inline-flex items-center gap-2 text-sm transition-colors ${
            isDark ? 'text-neutral-400 hover:text-accent-400' : 'text-neutral-500 hover:text-accent-600'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to blog
        </Link>
      </motion.div>

      {/* Post header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <h1 className={`text-3xl font-bold mb-4 leading-tight ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <span className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
            <Calendar className="w-4 h-4" />
            {formatDate(post.published_at)}
          </span>
          <span className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
            <Clock className="w-4 h-4" />
            {post.reading_time} min read
          </span>
          <span className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
            <User className="w-4 h-4" />
            {post.author_name || post.author?.name || 'Amir Akrami'}
          </span>
        </div>

        {/* Tags */}
        {(post.categories?.length > 0 || post.tags?.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {post.categories?.map((cat) => (
              <span key={cat.id} className={`px-2.5 py-1 rounded-md text-xs font-medium ${isDark ? 'bg-accent-500/10 text-accent-400' : 'bg-accent-50 text-accent-600'}`}>
                {cat.name}
              </span>
            ))}
            {post.tags?.map((tag) => (
              <span key={tag.id} className={`px-2.5 py-1 rounded-md text-xs ${isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-100 text-neutral-600'}`}>
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </motion.header>

      {/* Featured image */}
      {post.featured_image && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8 rounded-xl overflow-hidden"
        >
          <img src={post.featured_image} alt={post.title} className="w-full h-auto" />
        </motion.div>
      )}

      {/* Article content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="markdown-content"
      >
        <div
          dangerouslySetInnerHTML={createMarkup(post.content)}
          className={`leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
        />
      </motion.div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`mt-10 pt-6 border-t flex items-center gap-6 ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}
      >
        <span className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
          <MessageCircle className="w-4 h-4" />
          {post.comment_count || 0} comments
        </span>
        <span className={`text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
          {post.view_count} views
        </span>
      </motion.div>

      {/* Comments */}
      <div className="mt-10">
        <CommentSection postId={post.id} />
      </div>
    </div>
  );
};

export default BlogPost;
